<template>
  <DetailCard
    v-if="!isLoading"
    key="urun-ozellikleri"
    title="Ürün Özellikleri"
    cardKey="product-specifications"
    :showUpdateButton="true"
    :isLoading="isLoading"
    @requestUpdate="handleRequestUpdate"
  >
    <div v-if="sortedAttributes.length > 0">
      <div class="features-grid">
        <!-- Sol Kolon -->
        <div class="left-column">
          <Input
            v-for="(attr, index) in leftColumnAttributes"
            :key="`left-${index}`"
            type="text"
            :label="getLabel(attr)"
            :value="attr.attributeValue || ''"
            disabled
          />
        </div>

        <!-- Sağ Kolon -->
        <div class="right-column">
          <Input
            v-for="(attr, index) in rightColumnAttributes"
            :key="`right-${index}`"
            type="text"
            :label="getLabel(attr)"
            :value="attr.attributeValue || ''"
            disabled
          />
        </div>
      </div>
    </div>
    <div v-if="!isLoading" class="info-message" :class="{ disabled: isProductDisabled }">
      <Icons name="InfoCircle" :color="isProductDisabled ? '#C7C7C7' : '#4FA4E5'" lg class="info-icon" />
      <span class="info-text">
        Özelliklerde eksik/hata olduğunu düşünüyorsanız 
        <span class="highlight-text" @click="handleRequestUpdate">Güncelleme Talep Et/Hata Bildir</span> 
        sayfasından düzenleme talep edebilirsiniz.
      </span>
    </div>
  </DetailCard>
  <ProductFeaturesSkeleton v-else />
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import DetailCard from './DetailCard.vue';
import { Input, Icons } from "@n11/so-ui-components";
import ProductFeaturesSkeleton from '@/components/Skeleton/ProductDetailSkeleton/ProductFeaturesSkeleton.vue'

const store = useStore();

const props = defineProps({
  productFeatures: {
    type: Array,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    default: false
  },
});

const emit = defineEmits(['requestUpdate']);

const isProductDisabled = computed(() => store.getters['products/isProductDisabled']);

const sortedAttributes = computed(() => {
  const withValue = props.productFeatures.filter(attr => attr.attributeValue !== null);
  const withoutValue = props.productFeatures.filter(attr => attr.attributeValue === null);

  return [...withValue, ...withoutValue];
});

const leftColumnAttributes = computed(() => {
  return sortedAttributes.value.filter((_, index) => index % 2 === 0);
});

const rightColumnAttributes = computed(() => {
  return sortedAttributes.value.filter((_, index) => index % 2 !== 0);
});

const getLabel = (attr) => {
  if (attr.isMandatory) {
    return `${attr.attributeName}*`;
  } else {
    return `${attr.attributeName} (Opsiyonel)`;
  }
};

const handleRequestUpdate = () => {
  if (isProductDisabled.value) return;
  emit('requestUpdate');
};

</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';

:deep(.without-category) {
  background: $colorD05 !important;
}

.info-text {
  align-items: center;
  gap: 8px;
  color: $colorN80;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  letter-spacing: -0.05px;
  span {
    font-weight: 600;
  }
}

.features-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  width: 100%;
  position: relative;
  
  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.left-column,
.right-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.left-column > :deep(.input-container),
.right-column > :deep(.input-container) {
  margin-bottom: 0;
}

.info-message {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 16px;
  &.disabled {
    .info-text {
      color: $colorN60;
      .highlight-text {
        color: $colorN50;
      }
    }
  }
}

.highlight-text {
  color: $colorM50;
  cursor: pointer;
}
</style>
